import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";

import WaitingScreen from "../../../components/teacher/waiting_screen.jsx";

import APIclient from "../../../api-client/index.js";
import StatementPage from "../../../components/teacher/statement_page.js";
import StatementsProgress from "../../../components/teacher/statements_progress.jsx";
import Controls from "../../../components/teacher/controls.js";
import {
  ObjectId,
  SessionStatus,
  SessionTempo,
  Stored,
  StudentOpinion,
  PopulatedVotingSession,
} from "../../../types.js";

export function Session({
  votingSession,
  status,
  participants,
  answers,
  setStatus,
  toggleComment,
}: {
  votingSession: PopulatedVotingSession;
  status: SessionStatus;
  participants: number;
  answers: Array<Stored<StudentOpinion>>;
  setStatus: (status: SessionStatus) => void;
  toggleComment: (answerID: ObjectId, hidden: boolean) => void;
}) {
  const navigate = useNavigate();
  const [currentStatementIndex, setCurrentStatementIndex] = useState<number>(
    votingSession.currentStatementIndex,
  );
  const currentStatement = useMemo(
    () => votingSession?.statements[currentStatementIndex],
    [votingSession, currentStatementIndex],
  );

  useEffect(() => {
    if (status === SessionStatus.Results) navigate("results");
  }, [status]);

  async function startSession() {
    await APIclient.sessions.update(votingSession.code, {
      state: SessionStatus.Ongoing,
    });
    setStatus(SessionStatus.Ongoing);
  }

  async function goToResults() {
    await APIclient.sessions.update(votingSession.code, {
      state: SessionStatus.Results,
    });
    navigate("results");
  }

  async function nextStatement() {
    const result = await goToStatement(currentStatementIndex + 1);
    if (result) return;

    await goToResults();
  }

  async function previousStatement() {
    await goToStatement(currentStatementIndex - 1);
  }

  async function goToStatement(index: number) {
    if (votingSession.statements[index]) {
      await APIclient.sessions.update(votingSession.code, {
        currentStatementIndex: index,
      });
      setCurrentStatementIndex(index);
      return true;
    } else {
      return false;
    }
  }

  return (
    <>
      {status === SessionStatus.Draft && (
        <WaitingScreen code={votingSession.code} participants={participants} />
      )}

      {status === SessionStatus.Ongoing &&
        (votingSession.tempo === SessionTempo.Student ? (
          <StatementsProgress
            statements={votingSession.statements}
            answers={answers}
            participants={participants}
          />
        ) : (
          <StatementPage
            votingSession={votingSession}
            statement={currentStatement}
            answers={answers}
            key={currentStatementIndex}
            toggleHiddenComment={toggleComment}
          />
        ))}

      {/* Controls */}
      <div className="voting-session__controls">
        <Controls
          startSession={startSession}
          status={status}
          tempo={votingSession?.tempo}
          goToResults={goToResults}
          nextStatement={nextStatement}
          previousStatement={previousStatement}
          currentStatementIndex={currentStatementIndex}
          statementsCount={votingSession.statements.length}
        />
      </div>
    </>
  );
}
